import { supabase } from "./lib/supabase/client";
import { type CommitEvent, type Task, type TaskMap } from "./Tasks";
import { nanoid } from "nanoid";

export type TaskUpdate = {
  id: string,
  title?:string,
  description?:string,
  priority?: number,
  status?: string,
  isExternal?: boolean,
  dueDate?: string,

  addDeps?: string[],
  removeDeps?: string[]
}

type DomainEvent =
  | { id: string, type: 'create', task: Task }
  | { id: string, type: 'delete' }
  | { id: string, type: 'update', task_update: TaskUpdate }
;

const EVENTS_TABLE_NAME = 'task_events'

export const generateID = () => nanoid(8);

export const getDefaultTask = (id : string | null) : Task => ({
  title: "Untitled task",
  id: id ?? generateID(),
  description: "",
  priority: 3,
  dependsOn: [],
  status: 'not started',
  isComplete: false,
  isBlocked: false,
  isExternal: false
});

let EVENT_QUEUE : DomainEvent[] = []
export const dumpQueue = () => {

    try {
        insertEvents(EVENT_QUEUE);
    } catch {
        return
    }

    // If successful, empty the queue
    EVENT_QUEUE = []
}

export const notifyCommits = (commits: CommitEvent[]) => {
    EVENT_QUEUE.push(...reduceCommitEvent(commits));
}

function reduceCommitEvent(commits: CommitEvent[]): DomainEvent[] {
  const events: DomainEvent[] = []

  for (const c of commits) {
    switch (c.type) {
      case 'add': {

        const task = c.task ?? getDefaultTask(c.id ?? null)
        events.push({ id: task.id, type: 'create', task })

        break
      }

      case 'delete': {
        events.push({ id: c.id, type: 'delete' })
        break
      }

      case 'update': {
        events.push({ id: c.id, type: 'update', task_update:  c.task})
        break
      }

      case 'block': {
        events.push({
          id: c.id,
          type: 'update',
          task_update: {
            id: c.id,
            addDeps: [c.blockerId]
          }
        })
        break
      }

      case 'unblock': {
        events.push({
          id: c.id,
          type: 'update',
          task_update: {
            id: c.id,
            removeDeps: [c.blockerId]
          }
        })
        break
      }

      default: {
        // exhaustiveness guard
        const _exhaustive: never = c
        throw new Error(`Unhandled CommitEvent: ${_exhaustive}`)
      }
    }
  }

  return events
}

async function insertEvents(events: DomainEvent[]) {
  const rows = events.map(e => ({
    task_id: e.id,
    type: e.type,
    payload:
      e.type === 'create' ? e.task :
        e.type === 'update' ? e.task_update :
          {}
  }))

  const { error } = await supabase.from(EVENTS_TABLE_NAME)
    .insert(rows);

  if (error) {
    console.error('Failed to insert events', error);
    throw error;
  }

}

type Snapshot = {
  snapshot: TaskMap,
  schema_version: number,
  
}

export async function insertSnapshot(tasks: TaskMap) {

    const rows : Snapshot = {
        snapshot: tasks,
        schema_version: 2
    }

    const { error } = await supabase.from('snapshots').insert(rows);

    if (error) {
        console.error('Failed to insert snapshot', error);
        throw error;
    }
}

function taskToDB(t: Task, userID): DBTask {
  return {
    task_id: t.id,
    user_id: userID,
    title: t.title,
    description: t.description,
    priority: t.priority,
    is_complete: t.isComplete,
    is_external: t.isExternal,
    due_date: t.dueDate
  }
}

const TASK_TABLE_NAME = "tasks";
const DEPENDENCY_TABLE_NAME = "dependencies";

type DBTask = {
  task_id: string,
  user_id: string,
  title: string,
  description: string,
  priority: number,
  is_complete: boolean,
  is_external: boolean,
  due_date?: string
}

type DBDep = {
  user_id: string,
  child_id: string,
  parent_id: string
}

export async function postTasks(tasks: TaskMap) {
  let user_id = (await supabase.auth.getUser()).data.user.id
  const rows : DBTask[] = Object.values(tasks)
    .map(t => {return {...taskToDB(t, user_id), json: t}})

  let res_tasks = await supabase.from(TASK_TABLE_NAME).upsert<DBTask>(rows)

  const deps : DBDep[] = Object.values(tasks)
    .flatMap(t => t.dependsOn.map(td => ({parent_id: t.id, child_id: td, user_id: user_id})))
  let res_deps = await supabase.from(DEPENDENCY_TABLE_NAME).upsert<DBDep>(deps)
  
  console.debug(res_tasks)
  console.debug(res_deps)

}

window['postTasks'] = postTasks

export async function applyIntentToDB(intent : CommitEvent) {

  let error;
  switch (intent.type) {
    case 'delete':
      supabase.from(TASK_TABLE_NAME).delete().eq('id', intent.id)
      break
    case 'add':
      error = await supabase.from(TASK_TABLE_NAME).upsert<DBTask>({
        task_id: intent.task.id,
        user_id: (await supabase.auth.getUser()).data.user.id,
        title: intent.task.title,
        description: intent.task.description,
        priority: intent.task.priority,
        is_complete: intent.task.isComplete,
        is_external: intent.task.isExternal,
      })
      console.debug("Upserted to supabase")
      console.debug(error)
      break;
    case 'update':
      error = await supabase.from(TASK_TABLE_NAME).upsert({
        task_id: intent.task.id,
        user_id: (await supabase.auth.getUser()).data.user.id,
        title: intent.task.title,
        description: intent.task.description,
        priority: intent.task.priority,
        is_complete: intent.task.isComplete,
        is_external: intent.task.isExternal,
      })
      console.debug("Upserted to supabase", error)
      break;
    default:
      console.warn("Intent not applied to DB", intent)
      break;
  }


}

export async function getSnapshot() : Promise<TaskMap> {

    const {data, error} = await supabase.from('snapshots')
        .select('*')
        .order('created_at', { ascending : false })
        .limit(1)
        .single();
    
    console.log("Retrieved snapshot", data.snapshot)
    if (error) {
        console.error('Failed to retrieve snapshot', error);
        throw error;
    }


    if (data.schema_version == 1) {
      const snap : TaskMap = data.snapshot;
      Object.entries(snap).forEach( ([k, v]) => {
        snap[k] = { ...v, isComplete: v.status == 'complete' }
      })
      //snap.values.isComplete = snap.values.status == 'complete';
      data.snapshot = snap;
    }


    return data.snapshot;


}

